import { Link } from "react-router-dom";
import { Gem, PencilRuler, Hand, ScrollText, Scale, MessagesSquare } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { imgProps } from "@/lib/api";

const STORY = "/assets/brand/hero-1.jpg";

const VALUES = [
  {
    icon: Gem,
    title: "Authentic Diamonds",
    text: "We work only with real diamonds and genuine materials. No imitations, and nothing presented as more than it is.",
  },
  {
    icon: PencilRuler,
    title: "Polki & In-House Design",
    text: "Polki is our foundation. Every design is developed in-house, reinterpreting traditional forms so they sit naturally in contemporary life.",
  },
  {
    icon: Hand,
    title: "Craftsmanship",
    text: "Each piece is made and finished by hand — setting, enamelling, polishing — and inspected at every stage. Precision is the process, not a claim.",
  },
  {
    icon: ScrollText,
    title: "Certification",
    text: "Certification records authenticity in terms that outlast any description. Documentation is available for qualifying pieces and shared with you during consultation.",
  },
  {
    icon: Scale,
    title: "Honest Pricing",
    text: "We hold transparent, considered pricing as a principle. We do not publish prices; we explain them — piece, materials and making — before you commit to anything.",
  },
  {
    icon: MessagesSquare,
    title: "Private Consultation",
    text: "Every piece deserves a conversation. A consultation covers the piece, its materials and gemstone detail, certification, availability, customisation and final pricing.",
  },
];

export default function About() {
  return (
    <div className="pb-24 pt-40 lg:pb-32" data-testid="about-page">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal>
          <p className="overline">About Merakee</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl lg:text-6xl">
            The house behind <span className="italic text-[#A58343]">the pieces</span>
          </h1>
        </Reveal>

        <div className="mt-20 grid items-center gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="space-y-6 text-base leading-relaxed text-[#7C7163]">
              <p>
                Merakee Fine Jewellery is a fine jewellery house built around a simple
                discipline: real diamonds, designed in-house, and shared through
                conversation rather than counters.
              </p>
              <p>
                We work online and by appointment. There is no showroom to walk into;
                instead there is a private consultation — unhurried, one-to-one —
                where you see pieces properly and ask everything.
              </p>
              <p>
                Our foundation is polki: uncut diamonds set in the jadau tradition,
                re-proportioned in-house for the way jewellery is worn now. Alongside
                it sit diamond necklaces, bracelets and rings — each made by hand,
                each documented where it qualifies.
              </p>
              <p className="font-serif text-xl italic text-[#262019]">
                Every piece deserves a conversation.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="relative">
              <img {...imgProps(STORY, "(max-width: 1024px) 100vw, 50vw")} alt="Merakee campaign — models wearing the house's diamond and emerald ensembles" loading="lazy" className="block h-auto w-full" />
            </div>
          </Reveal>
        </div>

        <div className="mt-24 grid gap-12 border-y border-[#A58343]/20 py-16 sm:grid-cols-3" data-testid="about-stats">
          {[
            ["30+", "Designs across four collections"],
            ["100%", "Real diamonds — no imitations"],
            ["1:1", "Every client, a private consultation"],
          ].map(([n, t], i) => (
            <Reveal key={n} delay={0.1 * i}>
              <p className="font-serif text-5xl font-light text-[#A58343]">{n}</p>
              <p className="mt-3 text-sm leading-relaxed text-[#7C7163]">{t}</p>
            </Reveal>
          ))}
        </div>

        <div className="mt-24 grid gap-x-16 gap-y-16 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-values">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={0.08 * i}>
              <div className="border-l border-[#A58343]/30 pl-8">
                <v.icon size={26} strokeWidth={1.25} className="text-[#A58343]" />
                <h3 className="mt-6 font-serif text-2xl font-medium text-[#262019]">{v.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-[#7C7163]">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="grain mt-24 border border-[#A58343]/25 bg-[#EFE8DC] px-8 py-20 text-center lg:px-16" data-testid="about-cta">
            <p className="overline">Begin the Conversation</p>
            <h2 className="mx-auto mt-6 max-w-2xl font-serif text-4xl font-light leading-tight text-[#262019]">
              See the pieces properly, ask everything, decide in your own time
            </h2>
            <Link to="/consultation" className="btn-gold-solid mt-10" data-testid="about-book-consultation">
              Book a Private Consultation
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
